// Up-country route plan: one fixed trip's drops ordered and cut into days.
// Pure -- no Firestore, no Vue -- so scripts/check-upcountry-plan.js can assert
// every rule, and the planner worker can run it beside dispatchPlan.
// Spec: docs/superpowers/specs/2026-10-12-upcountry-route-plan-design.md
const geo = require('./geo')
const T = require('./travelTime')
const U = require('./upcountry')
const R = require('./bookingRules')
const RJ = require('./returnJob')

// Up to this many drops every order is tried; above it, a local search from
// the closest-next order (2-opt reversals and single-drop moves).
const EXACT_MAX = 7

// Delivery's figures, 2026-10-11/12. Every one is a setting
// (upcountryPlanConfig/default); these are what a missing field falls back to.
const PLAN_DEFAULTS = Object.freeze({
  day1EarliestStart: '03:00',
  laterEarliestStart: '06:00',
  dayEnd: '18:00',
  shopWindow: Object.freeze({ open: '08:00', close: '17:00' }),
  homeWindow: Object.freeze({ open: '08:00', close: '18:00' }),
  dropMinutes: Object.freeze({ install: 30, drop: 30, unknown: 30 }),
  kmCap: Object.freeze({ day1: 500, later: 300 }),
  kmCapByGroup: Object.freeze({})
})

const DOW_TH = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']

const isNum = (v, lo, hi) => typeof v === 'number' && isFinite(v) && v >= lo && v <= hi
const clockOr = (v, d) => (typeof v === 'string' && T.toMinutes(v) != null ? v.trim() : d)

function normalizePlanConfig (raw) {
  const r = raw || {}
  const D = PLAN_DEFAULTS
  const win = (v, d) => {
    const open = clockOr(v && v.open, d.open)
    const close = clockOr(v && v.close, d.close)
    return T.toMinutes(open) < T.toMinutes(close) ? { open, close } : { open: d.open, close: d.close }
  }
  let day1EarliestStart = clockOr(r.day1EarliestStart, D.day1EarliestStart)
  let laterEarliestStart = clockOr(r.laterEarliestStart, D.laterEarliestStart)
  let dayEnd = clockOr(r.dayEnd, D.dayEnd)
  // A day must have room to drive, or a long leg would never move.
  if (!(T.toMinutes(day1EarliestStart) < T.toMinutes(dayEnd) && T.toMinutes(laterEarliestStart) < T.toMinutes(dayEnd))) {
    day1EarliestStart = D.day1EarliestStart
    laterEarliestStart = D.laterEarliestStart
    dayEnd = D.dayEnd
  }
  const dropMinutes = {}
  Object.keys(D.dropMinutes).forEach(k => {
    const v = r.dropMinutes && r.dropMinutes[k]
    dropMinutes[k] = isNum(v, 5, 240) ? v : D.dropMinutes[k]
  })
  const capOf = c => ({
    day1: isNum(c && c.day1, 50, 2000) ? c.day1 : D.kmCap.day1,
    later: isNum(c && c.later, 50, 2000) ? c.later : D.kmCap.later
  })
  const kmCapByGroup = {}
  const src = r.kmCapByGroup || {}
  U.GROUPS.forEach(g => {
    const c = src[g.key]
    if (!c) return
    const e = {}
    if (isNum(c.day1, 50, 2000)) e.day1 = c.day1
    if (isNum(c.later, 50, 2000)) e.later = c.later
    if (Object.keys(e).length) kmCapByGroup[g.key] = e
  })
  return {
    day1EarliestStart, laterEarliestStart, dayEnd,
    shopWindow: win(r.shopWindow, D.shopWindow),
    homeWindow: win(r.homeWindow, D.homeWindow),
    dropMinutes, kmCap: capOf(r.kmCap), kmCapByGroup
  }
}

// A trip's recommended km per day (kmCap keeps its stored name; it is no longer
// a limit): a group override wins over the default; a trip of several
// groups with overrides takes the smallest of them.
function capsFor (cfg, groups) {
  const pick = k => {
    const own = (groups || []).map(g => (cfg.kmCapByGroup[g] || {})[k]).filter(v => v != null)
    return own.length ? Math.min.apply(null, own) : cfg.kmCap[k]
  }
  return { day1: pick('day1'), later: pick('later') }
}

const weekday = ymd => new Date(ymd + 'T00:00:00Z').getUTCDay()
const legMinutes = km => T.freeFlowMinutes(km, T.HIGHWAY_SPEEDS)
const round1 = n => Math.round(n * 10) / 10
const has = (o, k) => Object.prototype.hasOwnProperty.call(o || {}, k)

// cardCode -> closedDays, from shopClosedDay rows. A code present (even with no
// days) marks the customer a shop: shop hours apply.
function closedMap (rows) {
  const out = {}
  ;(rows || []).forEach(r => { if (r && r.cardCode) out[String(r.cardCode).trim()] = (r.closedDays || []).slice() })
  return out
}

// One drop per place, as dispatchPlan.groupVisits does for Bangkok: the same
// ตำบล and the same SAP customer or phone is one stop and one unload, however
// many SOs it carries. No location, no merge.
const codeOf = o => String(o.cardCode || '').trim()
const telOf = o => R.normalizeTel(o.telephone || o.custTel)
function samePlace (a, b) {
  const loc = String(a.location || '').trim()
  if (!loc || loc !== String(b.location || '').trim()) return false
  return (!!codeOf(a) && codeOf(a) === codeOf(b)) || (!!telOf(a) && telOf(a) === telOf(b))
}
function groupDrops (orders) {
  const groups = []
  ;(orders || []).forEach(o => {
    const g = groups.find(members => members.some(m => samePlace(m, o)))
    if (g) g.push(o)
    else groups.push([o])
  })
  return groups
}

function prepare (orders, closed, cfg) {
  const stops = []
  const unplaceable = []
  groupDrops(orders).forEach(members => {
    // The first exact pin, else the first member that resolves at all; a member
    // that resolves to nothing is still at the same place as its partners.
    const placed = members.map(o => ({ o, p: geo.resolveBooking(o) })).filter(x => x.p && x.p.precision)
    const at = placed.find(x => x.p.precision === 'exact') || placed[0]
    if (!at) { members.forEach(o => unplaceable.push(o.id)); return }
    const shopCodes = members.map(codeOf).filter(c => c && has(closed, c))
    const shop = shopCodes.length > 0
    const w = shop ? cfg.shopWindow : cfg.homeWindow
    const closedDays = Array.from(new Set([].concat.apply([], shopCodes.map(c => closed[c] || [])))).sort((a, b) => a - b)
    const minutesOf = o => (has(cfg.dropMinutes, o.installMode) ? cfg.dropMinutes[o.installMode] : cfg.dropMinutes.unknown)
    stops.push({
      orderId: members[0].id,
      orderIds: members.map(o => o.id),
      point: { lat: at.p.lat, lng: at.p.lng },
      precision: at.p.precision,
      shop,
      closedDays,
      open: T.toMinutes(w.open),
      close: T.toMinutes(w.close),
      // One stop, one unload: the largest member's time, not the sum.
      minutes: Math.max.apply(null, members.map(minutesOf))
    })
  })
  return { stops, unplaceable }
}

function context (trip, cfg) {
  return {
    startDate: trip.startDate,
    cfg,
    caps: capsFor(cfg, trip.groups),
    early: [T.toMinutes(cfg.day1EarliestStart), T.toMinutes(cfg.laterEarliestStart)],
    end: T.toMinutes(cfg.dayEnd)
  }
}

const fresh = () => ({ day: 0, at: null, pos: geo.DEPOT, stopsToday: 0, km: 0, kmByDay: {}, dayStart: {} })
const clone = s => ({
  day: s.day, at: s.at, pos: s.pos, stopsToday: s.stopsToday, km: s.km,
  kmByDay: Object.assign({}, s.kmByDay), dayStart: Object.assign({}, s.dayStart)
})
const earlyOf = (ctx, day) => ctx.early[day === 0 ? 0 : 1]
// The day's km figure. A recommendation (delivery, 2026-10-12): a day over it
// is flagged KM_OVER, never cut -- only dayEnd ends a day's driving.
const kmRecommendedFor = (ctx, day) => (day === 0 ? ctx.caps.day1 : ctx.caps.later)
function newDay (s) { s.day++; s.at = null; s.stopsToday = 0 }

// When a day starts driving: its earliest start, or later if the rest of the
// leg fits the day's hours and would otherwise reach the drop before it opens.
function startDay (s, ctx, leftMin, openAt) {
  const e = earlyOf(ctx, s.day)
  const fits = leftMin <= ctx.end - e
  s.at = fits && openAt != null ? Math.max(e, openAt - leftMin) : e
  s.dayStart[s.day] = s.at
}

// Drive `km` from s.pos. Stops for the night at dayEnd and carries on the next
// day -- a leg can span days, sleeping on the road. The km figures do not stop
// it: they are recommendations, flagged on the day (KM_OVER) by shape.
function drive (s, km, ctx, openAt) {
  let leftKm = km
  let leftMin = legMinutes(km)
  if (s.at == null) startDay(s, ctx, leftMin, openAt)
  s.km += km
  while (leftKm > 1e-9) {
    const rate = leftKm / leftMin
    const can = Math.max(0, Math.min(leftKm, (ctx.end - s.at) * rate))
    if (can >= leftKm - 1e-9) {
      s.at += leftMin
      s.kmByDay[s.day] = (s.kmByDay[s.day] || 0) + leftKm
      return
    }
    const used = can / rate
    s.kmByDay[s.day] = (s.kmByDay[s.day] || 0) + can
    leftKm -= can
    leftMin -= used
    newDay(s)
    startDay(s, ctx, leftMin, openAt)
  }
}

// One drop. `auto` lets the day rules end a day before it; retime passes false.
function visit (st, s, ctx, auto) {
  const km = geo.roadDistanceKm(st.pos, s.point) || 0
  const attempt = from => {
    const t = clone(from)
    drive(t, km, ctx, s.open)
    return t
  }
  let t = attempt(st)
  const closeAt = Math.min(ctx.end, s.close)
  const late = x => Math.max(x.at, s.open) + s.minutes > closeAt
  if (auto && st.stopsToday > 0 && (t.day !== st.day || late(t))) {
    const next = clone(st)
    newDay(next)
    t = attempt(next)
  }
  let arrive = t.at
  if (late(t) && t.stopsToday === 0 && t.at > closeAt - s.minutes) {
    // Reached after closing on a day with no drop yet: wait at the customer
    // overnight and make it at the next day's opening.
    newDay(t)
    t.at = Math.max(earlyOf(ctx, t.day), s.open)
    t.dayStart[t.day] = t.at
    arrive = t.at
  }
  const begin = Math.max(t.at, s.open)
  const finish = begin + s.minutes
  const date = U.addDays(ctx.startDate, t.day)
  const flags = []
  if (s.closedDays.indexOf(weekday(date)) !== -1) flags.push('CLOSED_DAY')
  if (finish > closeAt + 1e-6) flags.push('WINDOW')
  if (s.precision !== 'exact') flags.push('NO_PIN')
  t.at = finish
  t.pos = s.point
  t.stopsToday++
  return { st: t, rec: { orderId: s.orderId, orderIds: s.orderIds, point: s.point, day: t.day, arrive, wait: begin - arrive, leave: finish, km, precision: s.precision, shop: s.shop, flags } }
}

// Walk `stops` in order from the depot. `forced` (retime): a Set of indexes that
// open a new day, and no day break of the planner's own between drops.
function simulate (stops, ctx, forced) {
  let st = fresh()
  const recs = []
  stops.forEach((s, i) => {
    if (forced && forced.has(i) && st.stopsToday > 0) {
      st = clone(st)
      newDay(st)
    }
    const v = visit(st, s, ctx, !forced)
    st = v.st
    recs.push(v.rec)
  })
  // No drop placed: nothing was driven, so there is no drive home and no time to report.
  if (!recs.length) {
    return { recs, home: { leaveDay: 0, leave: null, arriveDay: 0, arrive: null, km: 0 }, km: 0, kmByDay: {}, dayStart: {} }
  }
  const leaveDay = st.day
  const leave = st.at
  const homeKm = geo.roadDistanceKm(st.pos, geo.DEPOT) || 0
  const home = clone(st)
  drive(home, homeKm, ctx, null)
  return {
    recs,
    home: { leaveDay, leave, arriveDay: home.day, arrive: home.at, km: homeKm },
    km: home.km,
    // As they stood before the drive home: a day block shows its drops' driving,
    // and the run back is on the home line (and in the total km) only.
    kmByDay: st.kmByDay,
    dayStart: st.dayStart
  }
}

// Candidates rank by: closed-day conflicts, window misses, the day the truck is
// home, the day of the last drop, km, then "stray" -- pairs of drops out of
// closest-next order (from the depot, then from each drop: south,
// ประจวบคีรีขันธ์ first; east, ระยอง, จันทบุรี, ตราด). Fewer days and fewer km
// come first (delivery, 2026-10-12); closest-first is what delivery prefers
// when it costs neither. km within 2% of the smaller (at least 5 km) is a tie:
// the distance model is +-15% (ROAD_FACTOR), so a 10 km gain on an 860 km route
// is noise, and closest-first decides.
function score (sim, home) {
  let conflicts = 0
  let misses = 0
  sim.recs.forEach(r => {
    if (r.flags.indexOf('CLOSED_DAY') !== -1) conflicts++
    if (r.flags.indexOf('WINDOW') !== -1) misses++
  })
  const pos = sim.recs.map(r => home[r.orderId])
  let stray = 0
  for (let i = 0; i < pos.length; i++) for (let j = i + 1; j < pos.length; j++) if (pos[i] > pos[j]) stray++
  const lastDay = sim.recs.length ? sim.recs[sim.recs.length - 1].day : 0
  return [conflicts, misses, sim.home.arriveDay, lastDay, sim.km, stray]
}
const KM = 4 // the km key's index in score()
const kmTolerance = (a, b) => Math.max(5, 0.02 * Math.min(a, b))
function better (a, b) {
  for (let i = 0; i < a.length; i++) {
    const tol = i === KM ? kmTolerance(a[i], b[i]) : 0
    if (Math.abs(a[i] - b[i]) > tol) return a[i] < b[i]
  }
  return false
}

function permutations (arr) {
  if (arr.length <= 1) return [arr.slice()]
  const out = []
  arr.forEach((x, i) => {
    const rest = arr.slice(0, i).concat(arr.slice(i + 1))
    permutations(rest).forEach(p => out.push([x].concat(p)))
  })
  return out
}

// Closest next, from the depot and then from each drop in turn.
function nearestFirst (stops) {
  const left = stops.slice()
  const out = []
  let pos = geo.DEPOT
  while (left.length) {
    let bi = 0
    left.forEach((s, i) => { if (geo.roadDistanceKm(pos, s.point) < geo.roadDistanceKm(pos, left[bi].point)) bi = i })
    pos = left[bi].point
    out.push(left.splice(bi, 1)[0])
  }
  return out
}

function bestOrder (stops, ctx) {
  const base = nearestFirst(stops)
  const home = {}
  base.forEach((s, i) => { home[s.orderId] = i })
  let best = null
  const consider = order => {
    const sim = simulate(order, ctx, null)
    const sc = score(sim, home)
    if (!best || better(sc, best.sc)) best = { sim, sc, order }
    return best.order === order
  }
  if (stops.length <= EXACT_MAX) {
    consider(base)
    permutations(base).forEach(consider)
    return best.sim
  }
  // Above EXACT_MAX: from closest-next, keep any 2-opt reversal or single-drop
  // move that ranks better, until none does.
  consider(base)
  for (let pass = 0, improved = true; improved && pass < 50; pass++) {
    improved = false
    const n = stops.length
    for (let i = 0; i < n - 1; i++) {
      for (let j = i + 1; j < n; j++) {
        const o = best.order
        if (consider(o.slice(0, i).concat(o.slice(i, j + 1).reverse(), o.slice(j + 1)))) improved = true
      }
    }
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) continue
        const o = best.order.slice()
        const [x] = o.splice(i, 1)
        o.splice(j, 0, x)
        if (consider(o)) improved = true
      }
    }
  }
  return best.sim
}

const clk = m => (m == null ? '' : T.toClock(m))
const round6 = n => Math.round(n * 1e6) / 1e6

function shape (sim, trip, ctx, unplaceable, orders) {
  const lastDay = sim.recs.length ? sim.recs[sim.recs.length - 1].day : -1
  const days = []
  for (let d = 0; d <= lastDay; d++) {
    const recs = sim.recs.filter(r => r.day === d)
    const start = sim.dayStart[d]
    const km = round1(sim.kmByDay[d] || 0)
    const kmRecommended = kmRecommendedFor(ctx, d)
    days.push({
      date: U.addDays(ctx.startDate, d),
      start: start == null ? '' : clk(start),
      end: recs.length ? clk(recs[recs.length - 1].leave) : '',
      km,
      kmRecommended,
      flags: km > kmRecommended ? ['KM_OVER'] : [],
      stops: recs.map(r => ({
        orderId: r.orderId, orderIds: r.orderIds.slice(), arrive: clk(r.arrive), wait: Math.round(r.wait), leave: clk(r.leave),
        km: round1(r.km), precision: r.precision, shop: r.shop, flags: r.flags,
        point: { lat: round6(r.point.lat), lng: round6(r.point.lng) }
      }))
    })
  }
  const suggestedReturn = U.addDays(ctx.startDate, sim.home.arriveDay)
  const warnings = []
  if (trip.returnDate && suggestedReturn > trip.returnDate) warnings.push('PAST_RETURN')
  if (unplaceable.length) warnings.push('UNPLACEABLE')
  return {
    ok: true,
    days,
    home: { leaveDate: U.addDays(ctx.startDate, sim.home.leaveDay), leave: clk(sim.home.leave),
      arriveDate: suggestedReturn, arrive: clk(sim.home.arrive), km: round1(sim.home.km) },
    suggestedReturn,
    km: round1(sim.km),
    unplaceable,
    warnings,
    orderIds: (trip.orderIds || (orders || []).map(o => o.id)).slice().sort(),
    cfgUsed: ctx.cfg
  }
}

function planTrip ({ trip, orders, closed, cfg }) {
  const c = normalizePlanConfig(cfg)
  const ctx = context(trip, c)
  const { stops, unplaceable } = prepare(orders, closed || {}, c)
  const sim = stops.length ? bestOrder(stops, ctx) : simulate([], ctx, null)
  return shape(sim, trip, ctx, unplaceable, orders)
}

// Orders laid out as `sequence` lists them -- days of order ids, an order the
// sequence leaves out joining its last day -- with each listed day opening a
// new one. Shared by retime and planInOrder. Any member's id finds its drop,
// and a drop is placed once, where it first appears.
function lay (stops, sequence) {
  const byId = {}
  stops.forEach(s => s.orderIds.forEach(id => { byId[id] = s }))
  const used = new Set()
  const ordered = []
  const forced = new Set()
  ;(sequence || []).forEach(day => {
    let first = true
    ;(day || []).forEach(id => {
      const s = byId[id]
      if (!s || used.has(s)) return
      used.add(s)
      if (first && ordered.length) forced.add(ordered.length)
      first = false
      ordered.push(s)
    })
  })
  stops.forEach(s => { if (!used.has(s)) ordered.push(s) })
  return { ordered, forced }
}

// Staff's own order and day breaks; the planner adds no break between drops
// staff put together (a late one is flagged WINDOW instead).
function retime ({ trip, orders, closed, cfg, sequence }) {
  const c = normalizePlanConfig(cfg)
  const ctx = context(trip, c)
  const { stops, unplaceable } = prepare(orders, closed || {}, c)
  const { ordered, forced } = lay(stops, sequence)
  return shape(simulate(ordered, ctx, forced), trip, ctx, unplaceable, orders)
}

// A saved plan brought up to the trip's current orders: staff's order and day
// breaks kept, an order no longer on the trip dropped, and each new drop slotted
// in where it ranks best (every position in every saved day, or a new last day,
// scored like planTrip). A new order at a saved drop's place joins that drop.
// `changes` names what moved since the save, for the dialog's banner.
function refitPlan ({ trip, orders, closed, cfg, stored }) {
  const c = normalizePlanConfig(cfg)
  const ctx = context(trip, c)
  const { stops, unplaceable } = prepare(orders, closed || {}, c)
  const now = (trip && trip.orderIds) || (orders || []).map(o => o.id)
  const was = (stored && stored.orderIds) || []
  const byId = {}
  stops.forEach(s => s.orderIds.forEach(id => { byId[id] = s }))
  const used = new Set()
  const days = []
  ;((stored && stored.days) || []).forEach(d => {
    const day = []
    ;(d.stops || []).forEach(s => idsOf(s).forEach(id => {
      const st = byId[id]
      if (st && !used.has(st)) { used.add(st); day.push(st) }
    }))
    if (day.length) days.push(day)
  })
  const home = {}
  nearestFirst(stops).forEach((s, i) => { home[s.orderId] = i })
  const run = ds => {
    const ordered = []
    const forced = new Set()
    ds.forEach(day => {
      if (!day.length) return
      if (ordered.length) forced.add(ordered.length)
      day.forEach(s => ordered.push(s))
    })
    return simulate(ordered, ctx, forced)
  }
  stops.filter(s => !used.has(s)).forEach(s => {
    const cands = []
    days.forEach((day, di) => {
      for (let i = 0; i <= day.length; i++) {
        const ds = days.map(d => d.slice())
        ds[di].splice(i, 0, s)
        cands.push(ds)
      }
    })
    cands.push(days.map(d => d.slice()).concat([[s]]))
    let best = null
    cands.forEach(ds => {
      const sc = score(run(ds), home)
      if (!best || better(sc, best.sc)) best = { ds, sc }
    })
    days.splice(0, days.length, ...best.ds)
  })
  const plan = shape(run(days), trip, ctx, unplaceable, orders)
  plan.changes = {
    added: now.filter(id => was.indexOf(id) === -1),
    removed: was.filter(id => now.indexOf(id) === -1)
  }
  return plan
}

// One given order of drops, with the day rules cutting the days -- what
// planTrip scores for each candidate. For checks, and to compare a plan with
// the order staff would have driven.
function planInOrder ({ trip, orders, closed, cfg, orderIds }) {
  const c = normalizePlanConfig(cfg)
  const ctx = context(trip, c)
  const { stops, unplaceable } = prepare(orders, closed || {}, c)
  const { ordered } = lay(stops, [orderIds || []])
  return shape(simulate(ordered, ctx, null), trip, ctx, unplaceable, orders)
}

// --- the dialog's edits ------------------------------------------------------

const sequenceOf = plan => ((plan && plan.days) || []).map(d => d.stops.map(s => s.orderId)).filter(d => d.length)

// One drop moved: 'up' / 'down' within its day, 'prevDay' / 'nextDay' to the
// end / start of the neighbouring day (a new last day if there is none).
function moveStop (sequence, orderId, dir) {
  const seq = sequence.map(d => d.slice())
  const di = seq.findIndex(d => d.indexOf(orderId) !== -1)
  if (di === -1) return seq
  const day = seq[di]
  const i = day.indexOf(orderId)
  if (dir === 'up' && i > 0) { day.splice(i, 1); day.splice(i - 1, 0, orderId) }
  else if (dir === 'down' && i < day.length - 1) { day.splice(i, 1); day.splice(i + 1, 0, orderId) }
  else if (dir === 'prevDay' && di > 0) { day.splice(i, 1); seq[di - 1].push(orderId) }
  else if (dir === 'nextDay') {
    day.splice(i, 1)
    if (di === seq.length - 1) seq.push([orderId])
    else seq[di + 1].unshift(orderId)
  }
  return seq.filter(d => d.length)
}

// --- saving ------------------------------------------------------------------

const sameSet = (a, b) => {
  const x = (a || []).slice().sort()
  const y = (b || []).slice().sort()
  return x.length === y.length && x.every((v, i) => v === y[i])
}
const isYmd = s => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
// Every order a stop delivers. A plan saved before drops merged has no orderIds.
const idsOf = s => (Array.isArray(s.orderIds) && s.orderIds.length ? s.orderIds : [s.orderId])

// `today` matters only for a sent trip, which may be re-planned until its
// loading day (U.checkSentEdit); a sent trip with no `today` is refused.
function checkPlanSave (trip, plan, today) {
  if (!trip) return U.fail('UP_PLAN_STALE')
  if (trip.status === 'sent') {
    // No date to judge the window by is a refusal; outside it, the window's own
    // reason (UP_SENT_LOCKED), so the message says why rather than "re-open".
    if (!today) return U.fail('UP_PLAN_STALE')
    const edit = U.checkSentEdit(trip, today)
    if (!edit.ok) return edit
  } else if (trip.status !== 'fixed') return U.fail('UP_PLAN_STALE')
  if (!plan || !sameSet(trip.orderIds, plan.orderIds)) return U.fail('UP_PLAN_STALE')
  if (!isYmd(plan.suggestedReturn) || plan.suggestedReturn < trip.startDate) return U.fail('UP_PLAN_BAD')
  const seen = {}
  for (const d of plan.days || []) {
    if (!isYmd(d.date) || d.date < trip.startDate) return U.fail('UP_PLAN_BAD')
    for (const s of d.stops || []) {
      for (const id of idsOf(s)) {
        if ((trip.orderIds || []).indexOf(id) === -1 || seen[id]) return U.fail('UP_PLAN_BAD')
        seen[id] = true
      }
    }
  }
  return { ok: true }
}

// ยกเลิกแผน: only a fixed trip -- once sent, the route may already be keyed in
// managecar -- and only one that holds a plan.
function checkPlanClear (trip) {
  if (!trip || trip.status !== 'fixed') return U.fail('UP_PLAN_SENT')
  if (!trip.plan) return U.fail('UP_PLAN_NONE')
  return { ok: true }
}

// What is stored on the trip: the days and times, not the flags or km, which
// are re-derived (retime) whenever the plan is shown.
function storedPlan (plan) {
  return {
    days: plan.days.map(d => ({ date: d.date, start: d.start, stops: d.stops.map(s => ({ orderId: s.orderId, orderIds: idsOf(s).slice(), arrive: s.arrive, leave: s.leave })) })),
    suggestedReturn: plan.suggestedReturn,
    orderIds: plan.orderIds.slice()
  }
}

function orderDates (plan) {
  const out = {}
  ;((plan && plan.days) || []).forEach(d => d.stops.forEach(s => idsOf(s).forEach(id => { out[id] = d.date })))
  return out
}

const planIsCurrent = trip => !!(trip && trip.plan && sameSet(trip.orderIds, trip.plan.orderIds))

// คัดลอกรายการให้จัดรถ's rows for a trip with a current plan: day number, stop
// number and ETA, in driving order; an order the plan does not place last,
// with blanks. null when there is no current plan.
function handoverRows (trip, orders) {
  if (!planIsCurrent(trip)) return null
  const byId = {}
  ;(orders || []).forEach(o => { byId[o.id] = o })
  const rows = []
  // Each member of a drop gets its own row, with the drop's day, stop and time.
  trip.plan.days.forEach((d, di) => d.stops.forEach((s, si) => idsOf(s).forEach(id => {
    if (byId[id]) { rows.push({ day: di + 1, date: d.date, seq: si + 1, arrive: s.arrive, order: byId[id] }); delete byId[id] }
  })))
  Object.keys(byId).forEach(id => rows.push({ day: '', date: '', seq: '', arrive: '', order: byId[id] }))
  return rows
}

// --- ส่งเข้าจัดรถ -------------------------------------------------------------

// Each order's place in the saved plan, for the rows the send writes:
// orderId -> { planDate, planSeq, serviceTime }. planSeq counts drops across
// the whole trip, so members of one drop share it; serviceTime is the drop's
// arrival window, which is what UpdateSo and the row's `time` carry. null when
// the plan no longer matches the trip.
function sendPlan (trip) {
  return planIsCurrent(trip) ? planSlots(trip.plan) : null
}

function planSlots (plan) {
  const out = {}
  let seq = 0
  plan.days.forEach(d => d.stops.forEach(s => {
    seq++
    const serviceTime = s.arrive && s.leave ? s.arrive + ' - ' + s.leave : (s.arrive || null)
    idsOf(s).forEach(id => { out[id] = { planDate: d.date, planSeq: seq, serviceTime } })
  }))
  return out
}

// A copy of the plan without one order: out of the plan's and its stop's
// orderIds; the stop's orderId passes to its first remaining member; an
// emptied stop and an emptied day are dropped.
function planWithout (plan, orderId) {
  if (!plan) return null
  const days = []
  ;(plan.days || []).forEach(d => {
    const stops = []
    d.stops.forEach(s => {
      const rest = idsOf(s).filter(id => id !== orderId)
      if (!rest.length) return
      stops.push(Object.assign({}, s, { orderId: s.orderId === orderId ? rest[0] : s.orderId, orderIds: rest }))
    })
    if (stops.length) days.push(Object.assign({}, d, { stops }))
  })
  return Object.assign({}, plan, { orderIds: (plan.orderIds || []).filter(id => id !== orderId), days })
}

// The deliveryList rows a re-plan moves. The reference is what `before` (the
// plan last written to the sheet) held for the order, else the row itself, so
// a time typed by hand on an unmoved row is left alone. An order new since
// `before` is skipped: the send writes it.
function sheetChanges ({ before, after, rows }) {
  const a = planSlots(after)
  const b = before ? planSlots(before) : null
  const out = []
  ;(rows || []).forEach(r => {
    const id = r.sourceSlotId
    const now = a[id]
    if (!now) return
    let ref
    if (b) {
      if (!b[id]) return
      ref = { planDate: b[id].planDate, planSeq: b[id].planSeq, time: b[id].serviceTime }
    } else ref = { planDate: r.planDate, planSeq: r.planSeq, time: r.time }
    const timeChanged = (ref.time || null) !== (now.serviceTime || null)
    if (ref.planDate !== now.planDate || ref.planSeq !== now.planSeq || timeChanged) {
      // The window only when it changed, and never blank: a time typed on the
      // row by hand survives a re-plan that only renumbers the stop.
      const patch = { planDate: now.planDate, planSeq: now.planSeq }
      if (timeChanged && now.serviceTime) patch.time = now.serviceTime
      out.push({ rowId: r.id, sourceSlotId: id, patch, timeChanged })
    }
  })
  return out
}

// The plan's suggested return when it is later than the trip's, else null.
function longerReturn (trip, plan) {
  return plan && isYmd(plan.suggestedReturn) && trip && plan.suggestedReturn > trip.returnDate ? plan.suggestedReturn : null
}

// Whether a SAP SO or RT (GetSo / GetRT row) carries this trip number -- what
// ยกเลิกการส่ง asks before CancelSo / CancelRt on an order the send may never
// have reached, since those clear whatever number is there.
function stampedWith (doc, transNo) {
  const n = doc && doc.transNo
  return n != null && n !== '' && transNo != null && String(n).trim() === String(transNo).trim()
}

// What ส่งเข้าจัดรถ refuses, decided before anything is written. `sap` maps
// orderId -> { so, rt, error } as the view read them (GetSo, GetRT). Every
// problem is named, not only the first. An exchange whose RT is cancelled or
// unreadable is a WARNING: the SO goes, without the return -- the rule
// คิวรถเล็ก's confirm keeps. `retry` is the pass over a trip already locked:
// the trip is then `sent` and its own number on an SO or RT is not "dispatched".
function checkSend ({ trip, orders, sap, retry }) {
  const t = trip || {}
  const none = reason => ({ ok: false, reason, problems: [], warnings: [] })
  if (retry ? (t.status !== 'sent' || !t.transNo) : t.status !== 'fixed') return none('UP_SEND_STATUS')
  if (!planIsCurrent(t)) return none('UP_PLAN_STALE')
  const mine = n => retry && n != null && String(n) === String(t.transNo)
  const free = n => n == null || n === '' || mine(n)
  // the trip's own number excuses only DISPATCHED: a cancelled or closed RT stays one
  const rtOk = rt => { const p = RJ.rtProblem(rt); return !p || (p === 'DISPATCHED' && mine(rt.transNo)) }
  const problems = []
  const warnings = []
  ;(orders || []).forEach(o => {
    const s = (sap || {})[o.id] || {}
    const say = (list, text) => list.push({ orderId: o.id, text: U.handoverRef(o) + ': ' + text })
    if (s.error) return say(problems, s.error)
    const kind = RJ.jobTypeOf(o)
    if (kind === 'return') {
      if (!s.rt) return say(problems, 'ไม่พบ RT ใน SAP')
      if (!rtOk(s.rt)) return say(problems, RJ.rtProblemText(s.rt))
      return
    }
    if (!s.so) return say(problems, 'ไม่พบ SO ใน SAP')
    if (s.so.canceled === 'Y') return say(problems, 'SO ถูกยกเลิกแล้ว')
    if (s.so.status === 'C') return say(problems, 'SO ถูกปิดแล้ว')
    if (!free(s.so.transNo)) return say(problems, 'อยู่ในใบจัดรถเลขที่ ' + s.so.transNo + ' แล้ว')
    const soRt = s.so.rtNo == null ? '' : String(s.so.rtNo).trim()
    if (soRt && soRt !== String(o.rtNo == null ? '' : o.rtNo).trim()) {
      say(warnings, 'SO ระบุ RT ' + soRt + ' ที่ยังไม่อยู่ในออเดอร์นี้ — กด อัปเดตจาก SAP ก่อนส่ง ถ้าต้องการรับคืนในรอบนี้')
    }
    if (kind === 'exchange' && o.rtNo) {
      if (!s.rt) say(warnings, 'อ่าน RT ' + o.rtNo + ' ไม่ได้ — ส่งเฉพาะ SO')
      else if (!rtOk(s.rt)) say(warnings, RJ.rtProblemText(s.rt) + ' — ส่งเฉพาะ SO')
    }
  })
  return { ok: !problems.length, reason: problems.length ? 'UP_SEND_PROBLEMS' : null, problems, warnings }
}

// --- map links ---------------------------------------------------------------

// One drop on Google Maps -- dispatchPlan.mapUrlForBooking's rule: coordinates
// only for a real pin, else the written address, which Google geocodes far
// better than a ตำบล centroid. null when there is nothing to search.
function placeUrl (stop, order) {
  const s = stop || {}
  const o = order || {}
  let target = ''
  if (s.precision === 'exact' && s.point) target = s.point.lat + ',' + s.point.lng
  else {
    const loc = R.parseLocation(o.location)
    const where = loc ? [loc.subDt, loc.district, loc.province] : [o.location]
    target = [o.address || o.custAddress].concat(where).filter(Boolean).join(' ').trim()
  }
  return target ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(target) : null
}

// The Maps directions URL holds at most nine waypoints (dispatchPlan's
// MAX_WAYPOINTS); a longer day is split into parts that chain end to start.
const MAX_WAYPOINTS = 9
const dirUrl = points => 'https://www.google.com/maps/dir/?api=1' +
  '&travelmode=driving' +
  '&origin=' + encodeURIComponent(points[0]) +
  '&destination=' + encodeURIComponent(points[points.length - 1]) +
  (points.length > 2 ? '&waypoints=' + points.slice(1, -1).map(encodeURIComponent).join('%7C') : '')

// One link (or several parts) per day with drops: from where the truck slept --
// the depot on the first -- through the day's drops, and home to the depot on
// the day of the last drop.
function dayRouteUrls (plan) {
  const depot = geo.DEPOT.lat + ',' + geo.DEPOT.lng
  const pt = s => s.point.lat + ',' + s.point.lng
  const days = ((plan && plan.days) || []).map(d => ({ date: d.date, stops: (d.stops || []).filter(s => s.point) })).filter(d => d.stops.length)
  let from = depot
  return days.map((d, i) => {
    const points = [from].concat(d.stops.map(pt))
    if (i === days.length - 1) points.push(depot)
    from = pt(d.stops[d.stops.length - 1])
    const parts = []
    for (let a = 0; a < points.length - 1;) {
      const b = Math.min(a + MAX_WAYPOINTS + 1, points.length - 1)
      parts.push(dirUrl(points.slice(a, b + 1)))
      a = b
    }
    return { date: d.date, parts }
  })
}

// --- the closed-day list -----------------------------------------------------

function checkShopClosedDay ({ cardCode, cardName, closedDays }) {
  const code = String(cardCode || '').trim()
  if (!code || code.length > 30 || /[/\s]/.test(code)) return U.fail('SHOP_BAD_CODE')
  const name = String(cardName || '').trim()
  if (!name || name.length > 200) return U.fail('SHOP_BAD_NAME')
  const days = Array.isArray(closedDays) ? closedDays : null
  if (!days || days.some(d => !Number.isInteger(d) || d < 0 || d > 6)) return U.fail('SHOP_BAD_DAYS')
  return { ok: true, cardCode: code, cardName: name, closedDays: Array.from(new Set(days)).sort((a, b) => a - b) }
}

// "ศุกร์", "ศุกร์, อาทิตย์", "อา." -> [5], [0, 5], [0]. null if any word is not a day.
const DAY_WORDS = [
  ['อาทิตย์', 'อา', 'sun', 'sunday'], ['จันทร์', 'จ', 'mon', 'monday'], ['อังคาร', 'อ', 'tue', 'tuesday'],
  ['พุธ', 'พ', 'wed', 'wednesday'], ['พฤหัสบดี', 'พฤหัส', 'พฤ', 'thu', 'thursday'], ['ศุกร์', 'ศ', 'fri', 'friday'],
  ['เสาร์', 'ส', 'sat', 'saturday']
]
function parseDays (text) {
  const words = String(text || '').split(/[,\s/]+/).map(w => w.replace(/^วัน/, '').replace(/\.$/, '').trim().toLowerCase()).filter(Boolean)
  const out = []
  for (const w of words) {
    const i = DAY_WORDS.findIndex(list => list.indexOf(w) !== -1)
    if (i === -1) return null
    if (out.indexOf(i) === -1) out.push(i)
  }
  return out.sort((a, b) => a - b)
}

const daysText = days => (days || []).map(d => DOW_TH[d]).join(', ')

module.exports = {
  EXACT_MAX, PLAN_DEFAULTS, DOW_TH, normalizePlanConfig, capsFor, closedMap,
  planTrip, retime, planInOrder, sequenceOf, moveStop,
  refitPlan, checkPlanSave, checkPlanClear, storedPlan, orderDates, planIsCurrent, handoverRows, sendPlan, planSlots, planWithout, sheetChanges, longerReturn, checkSend, stampedWith,
  placeUrl, dayRouteUrls,
  checkShopClosedDay, parseDays, daysText, weekday
}
